import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { ToggleSwitchComponent } from '../../../core/controls/toggle-switch.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { TogglePanelComponent } from '../../../core/panels/toggle-panel.component';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { TableComponent } from '../../../core/table/table.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { RestrictionsInfo } from '../../../../models/api/view/claim/details/accommodations/restrictions/restrictions-info';

@Component({
  selector: 'app-restrictions',
  imports: [
    CardComponent,
    PageHeaderComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TogglePanelComponent,
    ToggleSwitchComponent,
    TableComponent,
    ColumnComponent,
  ],
  template: `
    <app-page-header label="ESSENTIAL JOB FUNCTION">
      <ng-template #right>
        <app-toggle-switch
          label="Expand all"
          [control]="togglePanel.expandAll"
          [showHelpText]="false"
          (click)="togglePanel.toggle()"
        />
      </ng-template>
    </app-page-header>
    <app-toggle-panel [showExpand]="false" #togglePanel>
      @for (
        accommodationRestriction of accommodationRestrictions;
        track accommodationRestriction;
        let restrict = $index
      ) {
        @for (
          restriction of accommodationRestriction?.restrictionDetails;
          track restriction;
          let restrDetails = $index
        ) {
          <app-card
            [label]="restriction.jobTask"
            [expanded]="restrict === 0 && restrDetails === 0"
            labelCase="none"
            [showExpand]="true"
            type="Medium"
            [rowClick]="true"
            theme="Light"
          >
            <div class="tw-w-full tw-mb-4">
              <h3>RESTRICTIONS DETAILS</h3>
              <hr />
            </div>
            <div class="tw-flex tw-gap-4">
              <app-card class="tw-w-[33%]" label="IMPACTED PERIOD">
                <app-detail-panel [dataSource]="restriction" class="tw-w-full ">
                  <app-detail-item
                    label="BEGIN DATE"
                    name="impactedBeginDate"
                    format="d"
                  />
                  <app-detail-item
                    label="END DATE"
                    name="impactedEndDate"
                    format="d"
                  />
                  <app-detail-item label="LONG TERM" name="impactedLongTerm" />
                </app-detail-panel>
              </app-card>
              <app-card class="tw-w-[33%]" label="RESTRICTION PERIOD">
                <app-detail-panel [dataSource]="restriction" class="tw-w-full ">
                  <app-detail-item
                    label="BEGIN DATE"
                    name="restrictionBeginDate"
                    format="d"
                  />
                  <app-detail-item
                    label="END DATE"
                    name="restrictionEndDate"
                    format="d"
                  />
                  <app-detail-item
                    label="LONG TERM"
                    name="restrictionLongTerm"
                  />
                </app-detail-panel>
              </app-card>
              <app-card class="tw-w-[33%]" label="DETAILS">
                <app-detail-panel [dataSource]="restriction" class="tw-w-full ">
                  <app-detail-item
                    label="MEDICAL NEEDED"
                    name="medicalNeeded"
                  />
                </app-detail-panel>
                <app-detail-panel
                  [dataSource]="
                    accommodationRestriction
                      ?.cognitiveRestrictionDetails?.[0] ?? {}
                  "
                  class="tw-w-full "
                >
                  <app-detail-item
                    label="COGNITIVE RESTRICTIONS"
                    name="description"
                  />
                </app-detail-panel>
                <app-detail-panel
                  [dataSource]="
                    accommodationRestriction?.physicalRestrictionDetails?.[0] ??
                    {}
                  "
                  class="tw-w-full "
                >
                  <app-detail-item
                    label="PHYSICAL RESTRICTIONS"
                    name="description"
                  />
                </app-detail-panel>
              </app-card>
            </div>
            <div class="tw-w-full tw-mt-4 tw-flex tw-gap-4 tw-mb-8 tw-pb-2">
              <app-card
                class="tw-w-[32%]"
                label="MAJOR LIFE ACTIVITIES/BODILY FUNCTIONS"
              >
                <ul class="tw-ml-4" style="list-style-type: disc;">
                  @for (
                    activity of accommodationRestriction?.majorLifeActivityDetails;
                    track activity
                  ) {
                    <li class="tw-w-full">
                      {{ activity.description }}
                    </li>
                  }
                </ul>
              </app-card>
              <app-card class="tw-w-[68%]" label="RESTRICTION DESCRIPTION">
                <div class="tw-w-full">
                  {{ restriction.restrictionName }}
                </div>
              </app-card>
            </div>
            <div class="tw-w-full tw-mb-4 tw-mt-8">
              <h3>MEDICAL DETAILS</h3>
              <hr />
            </div>
            <app-table
              [dataSource]="accommodationRestriction?.restrictionMedicalDetails"
              [pageSize]="10"
              [scrollable]="false"
              dataKey="medicalRestrictionId"
              recordLabel="MEDICAL"
              [expandable]="true"
              [stripedRows]="false"
              [showExport]="false"
            >
              @if (showProviderFields) {
                <app-column
                  name="providerName"
                  label="PROVIDER NAME"
                  classes="tw-font-bold"
                />
              }
              <app-column name="medicalDueDate" label="DUE DATE" format="d" />
              <app-column
                name="medicalReceivedDate"
                label="RECEIVED DATE"
                format="d"
              />
              <app-column
                name="medicalReviewDate"
                label="REVIEWED DATE"
                format="d"
              />
              <app-column
                name="estimatedFullDutyDate"
                label="ESTIMATED FULL DUTY DATE"
                format="d"
              />
              <app-column
                name="nextAppointmentDate"
                label="NEXT APPOINTMENT DATE"
                format="d"
              />
              <ng-template #expand let-data>
                <div class="tw-flex tw-gap-4">
                  <app-card
                    label="CLARIFICATION"
                    [class]="showProviderFields ? 'tw-w-[50%]' : 'tw-w-full'"
                  >
                    <app-detail-panel [dataSource]="data" class="tw-w-full">
                      <app-detail-item
                        label="DUE DATE"
                        name="clarificationDueDate"
                        format="d"
                      />
                      <app-detail-item
                        label="SEND DATE"
                        name="clarificationSentDate"
                        format="d"
                      />
                      <app-detail-item
                        label="RECIEVED DATE"
                        name="clarificationReceivedDate"
                        format="d"
                      />
                      <app-detail-item
                        label="REVIEWED DATE"
                        name="clarificationReviewDate"
                        format="d"
                      />
                    </app-detail-panel>
                  </app-card>
                  @if (showProviderFields) {
                    <app-card label="PROVIDER" class="tw-w-[50%]">
                      <app-detail-panel [dataSource]="data" class="tw-w-full">
                        <app-detail-item label="NAME" name="providerName" />
                        <app-detail-item
                          label="PHONE"
                          name="providerPhone"
                          format="p"
                        />
                        <app-detail-item
                          label="FAX"
                          name="providerFax"
                          format="p"
                        />
                        <app-detail-item
                          label="ICD"
                          name="icdCode"
                          labelCase="none"
                        />
                      </app-detail-panel>
                    </app-card>
                  }
                </div>
              </ng-template>
            </app-table>
          </app-card>
          <div class="tw-pt-8"></div>
        }
      }
    </app-toggle-panel>
  `,
  styles: ``,
})
export class RestrictionsComponent extends BaseComponent implements OnInit {
  @ViewChild('togglePanel')
  togglePan: TogglePanelComponent;

  accommodationRestrictions: RestrictionsInfo[] = [];
  showProviderFields = false;

  async ngOnInit() {
    this.accommodationRestrictions =
      await this.api.accommodationRestrictions.restrictions(getClaimKey());

    this.accommodationRestrictions.forEach((restriction) => {
      this.combineDescriptions(restriction, 'physicalRestrictionDetails');
      this.combineDescriptions(restriction, 'cognitiveRestrictionDetails');
    });

    // API returns null for these fields when user has NMEQ_NN, NMEV, NMEV_NN, or NMEV_QUERY tokens
    this.showProviderFields = this.accommodationRestrictions.some(
      (restriction) =>
        restriction.restrictionMedicalDetails?.some(
          (medical) =>
            medical.providerName ||
            medical.providerPhone ||
            medical.providerFax ||
            medical.iCDCode,
        ),
    );

    this.view.hideLoading();
  }

  private combineDescriptions(
    restriction: RestrictionsInfo,
    key: 'physicalRestrictionDetails' | 'cognitiveRestrictionDetails',
  ) {
    const details = restriction[key];
    if (details?.length > 0) {
      const combinedDescription = details
        .map((d: any) => d.description)
        .join(', ');
      const first = details[0];

      (restriction[key] as any) = [
        {
          ...first,
          description: combinedDescription,
        },
      ];
    }
  }
}
